/*!
 * Copyright © 2018. Verizon Connect Ireland Limited. All rights reserved.
 */

import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, of } from 'rxjs';

import { IAssetsHttpService } from './assets-http.service.interface';
import { IAssetSelectionResponse, IAssetPlotResponse, IAssetDetailResponse } from '../../models';
import { catchError, map, shareReplay } from 'rxjs/operators';
import { DomSanitizer } from '@angular/platform-browser';
import { environment } from '../../../../environments/environment';
import { IAssetLocationHisoryRequestDto, IAssetLocationHistoryResponseDto } from '../../../replay/models/asset-location-history.interface';

@Injectable()
export class AssetsHttpService implements IAssetsHttpService {
  private readonly _rootUrl: string;

  constructor(private readonly _httpClient: HttpClient, private readonly _sanitizer: DomSanitizer) {
    this._rootUrl = environment.config.endpoints.assetsApiEndpoint;
  }

  public getSelection(): Observable<IAssetSelectionResponse[]> {
    return this._httpClient.get<IAssetSelectionResponse[]>(this._rootUrl + '/api/assets/selection');
  }

  public updateSelection(assetIds: number[]): Observable<boolean> {
    const headers = new HttpHeaders().set('Content-Type', 'application/json; charset=utf-8');
    return this._httpClient.put<boolean>(this._rootUrl + '/api/assets/selection', JSON.stringify(assetIds), { headers });
  }

  public getAssetPlots(dbId: number, vehicleTrackingIds: string[]): Observable<IAssetPlotResponse[]> {
    return this._httpClient.post<IAssetPlotResponse[]>(this._rootUrl + '/api/assetplots', {
      dbId,
      vehicleTrackingId: vehicleTrackingIds
    });
  }

  public getAssetsByIds(ids: number[]): Observable<IAssetDetailResponse[]> {
    return this._httpClient.post<IAssetDetailResponse[]>(this._rootUrl + '/api/assets/assets', {
      ids
    });
  }

  public getAssetImageById(id: number): Observable<string> {
    const requestOptions: object = {
      responseType: 'blob'
    };
    return this._httpClient.get<Blob>(this._rootUrl + `/api/assets/image/${id}`, requestOptions).pipe(
      map(image => this._sanitizer.bypassSecurityTrustUrl(URL.createObjectURL(image)) as string),
      catchError(() => of(null)),
      shareReplay(1)
    );
  }

  public getLocationHistory(request: IAssetLocationHisoryRequestDto): Observable<IAssetLocationHistoryResponseDto> {
    return this._httpClient
      .post<IAssetLocationHistoryResponseDto>(`${this._rootUrl}/api/v2/admin/assets/${request.assetTrackingId}/history/locations`, request)
      .pipe(catchError(() => of(null)));
  }
}
